import { getMediaUrl, PddPayloadTypes } from "@/constants";
import { Context } from "@/context";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import type { Manifest } from "@/types";
import { useFrame } from "@react-three/fiber";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type RefObject,
} from "react";
import {
  getRandomCollisionSoundPath,
  getSoundPaths,
  getSoundUrl,
  getTriggeredSoundPath,
  shouldSkipCollisionSound,
} from "../helpers/sounds";

export const useSoundEventHandler = (
  id: string,
  currentFrame: RefObject<number>,
  manifest: Manifest,
) => {
  const { dieType, lastStandardEvent, frameData } = useRenderedDiceStore(
    (state) => state.renderedDice.entities[id],
  );
  const { onDiceEvent, offDiceEvent } = useRenderedDiceStore();
  const volume = useRenderedDiceStore((state) => state.userSettings?.volume);
  const soundsObj = manifest.resources?.sounds;
  const soundPaths = useMemo(() => getSoundPaths(soundsObj), [soundsObj]);
  const { nodeEnv: ne, env: e, renderedWorkerPort } = useContext(Context);
  const messenger: Pick<typeof self, "postMessage"> =
    renderedWorkerPort ?? self;
  const nameJson = self.name ? JSON.parse(self.name) : {};
  const nodeEnv = ne || nameJson.nodeEnv;
  const env = e || nameJson.env;
  const mediaUrl = getMediaUrl(nodeEnv, env);
  const lastEventTypePlayedRef = useRef<string | null>(null);
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);

  const runSoundsForEvent = useCallback(
    (event?: string) => {
      if (!volume || !event || lastEventTypePlayedRef.current === event) return;

      const soundPath = getTriggeredSoundPath({
        dieType,
        event,
        manifest,
        soundPaths,
      });
      if (!soundPath) return;

      messenger.postMessage({
        type: PddPayloadTypes.PlaySound,
        payload: {
          url: getSoundUrl({
            customUrlMap,
            hasManifestSounds: true,
            mediaUrl,
            setCodename: manifest.slug,
            soundPath,
          }),
          volume: 1,
        },
      });

      lastEventTypePlayedRef.current = event;
    },
    [customUrlMap, dieType, manifest, mediaUrl, messenger, soundPaths, volume],
  );

  useEffect(() => {
    if (!(volume && onDiceEvent)) return;

    const handleEvent = ({
      id: eventId,
      trigger,
    }: {
      id: string;
      trigger?: string;
    }) => {
      if (eventId !== id) return;

      runSoundsForEvent(trigger);
    };

    onDiceEvent("event", handleEvent);

    return () => {
      if (offDiceEvent) {
        offDiceEvent("event", handleEvent);
      }
    };
  }, [id, offDiceEvent, onDiceEvent, runSoundsForEvent, volume]);

  useFrame(() => {
    if (!volume || shouldSkipCollisionSound(lastStandardEvent)) {
      return;
    }

    // Changes in collisionVolume indicate a new collision event
    const frameVolume = frameData?.[currentFrame.current]?.collisionVolume;

    if (!frameVolume) return;

    const soundPath = getRandomCollisionSoundPath(soundPaths);
    if (!soundPath) return;

    messenger.postMessage({
      type: PddPayloadTypes.PlaySound,
      payload: {
        url: getSoundUrl({
          customUrlMap,
          hasManifestSounds: Boolean(soundsObj),
          mediaUrl,
          setCodename: manifest.slug,
          soundPath,
        }),
        volume: frameVolume,
        isCollision: true,
      },
    });
  });
};
